/**
 * Audit UI Slice
 *
 * Redux slice for managing PURE UI state only.
 * Does NOT duplicate RTK Query server state.
 * Computed values should be derived via hooks/selectors from RTK Query cache.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// ============================================================================
// Types
// ============================================================================

export type SidebarMode = 'chat' | 'audit';

export interface AuditUiState {
  // Pure UI state only - no server state duplication
  highlightedSectionId: string | null; // Section ID to highlight rules for
  sidebarMode: SidebarMode; // Current sidebar mode
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: AuditUiState = {
  highlightedSectionId: null,
  sidebarMode: 'chat',
};

// ============================================================================
// Slice
// ============================================================================

export const auditUiSlice = createSlice({
  name: 'jotaudit/ui',
  initialState,
  reducers: {
    /**
     * Set the highlighted section (for scrolling/highlighting in audit sidebar)
     */
    setHighlightedSection: (state, action: PayloadAction<string>) => {
      state.highlightedSectionId = action.payload;
    },

    /**
     * Clear the highlighted section
     */
    clearHighlightedSection: state => {
      state.highlightedSectionId = null;
    },

    /**
     * Set the sidebar mode
     */
    setSidebarMode: (state, action: PayloadAction<SidebarMode>) => {
      state.sidebarMode = action.payload;
    },

    /**
     * Open audit sidebar and highlight a specific section's rules
     */
    openAuditForSection: (state, action: PayloadAction<string>) => {
      state.sidebarMode = 'audit';
      state.highlightedSectionId = action.payload;
    },

    /**
     * Reset UI state (e.g., when navigating away)
     */
    resetAuditUi: state => {
      state.highlightedSectionId = null;
      // Note: we don't reset sidebarMode as user preference should persist
    },
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  setHighlightedSection,
  clearHighlightedSection,
  setSidebarMode,
  openAuditForSection,
  resetAuditUi,
} = auditUiSlice.actions;

export default auditUiSlice.reducer;
